import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Typography, Grid, FormControlLabel } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { selectHasSitewiseAccess, selectHasRecalculationAccess, selectHasModifierAccess } from '../../../redux/authSlice';
import CommonTable from '../../../common_components/CommonTable';
import { SUMMARY_ROW_POSITIONS, EXPAND_TRIGGERS } from '../../../common_components/tableConstants';
import { NumberInput, ExportButton } from '../../../common_components/FormInputs';
import * as XLSX from 'xlsx-js-style';
import { selectTrackerData, selectTrackerFormulas, selectTrackerLoading, selectSavedModifiers, selectSavedProjections, savePerformanceTracker, fetchAccountPerformanceTracker, recalculatePerformanceTracker, selectTrackerClientId, selectTrackerMonth, } from '../../../redux/accountPerformanceTrackerSlice';
import AppButton from '../../../common_components/AppButton';
import CommonLoader from '../../../common_components/CommonLoader';
import MetricCard from '../../../common_components/MetricCard';
import InfoTooltip from '../../../common_components/InfoTooltip';
import { StyledCurrency } from '../../../helper/StyledCurrency';
import TableToolbar from '../../../common_components/TableToolbar';
import IOSSwitch from '../../../common_components/IOSSwitch';
import { buildTrackerRows, buildTrackerTotal, calcMetricCardValues, } from './performanceTrackerUtils';
// ✅ Reuse divergence preferences actions/selectors
import { fetchPreferences, savePreferences, selectPreferences, selectPreferencesLoading, updateNetworkPreference, } from '../../../redux/divergenceSlice';
// ─── Constants ────────────────────────────────────────────────────────────────
const TRACKER_NETWORK_KEY = 'account_performance_tracker';
// All metric column ids in default order (excluding 'date')
const ALL_TRACKER_METRICS = [
    'spend_meta',
    'spend_search',
    'spend_bing',
    'spend_criteo',
    'spend_daily',
    'spend_traffic_meta',
    'spend_traffic_search',
    'spend_traffic_total',
    'spend_goal_daily',
    'spend_mtd',
    'spend_goal_mtd',
    'spend_var',
    'rev_daily',
    'rev_mtd',
    'rev_modifier',
    'rev_goal_daily',
    'rev_goal_mtd',
    'rev_var',
    'roas_daily',
    'roas_mtd',
    'roas_goal_mtd',
    'roas_var',
];
// ─── Preference Helpers (same pattern as DivergenceTable) ────────────────────
const getHiddenColumnsFromPreferences = (preferences, networkKey) => {
    const hiddenSet = new Set();
    if (!preferences?.[networkKey])
        return hiddenSet;
    preferences[networkKey].forEach(item => {
        const metricKey = Object.keys(item)[0];
        const config = item[metricKey];
        if (config?.visibility === false) {
            hiddenSet.add(metricKey);
        }
    });
    return hiddenSet;
};
const getColumnOrderFromPreferences = (preferences, networkKey, defaultMetrics) => {
    if (!preferences?.[networkKey] || preferences[networkKey].length === 0) {
        return defaultMetrics;
    }
    const orderedMetrics = [];
    const preferencesKeys = [];
    preferences[networkKey].forEach(item => {
        const metricKey = Object.keys(item)[0];
        const config = item[metricKey];
        preferencesKeys.push(metricKey);
        if (config?.visibility !== false) {
            orderedMetrics.push({ key: metricKey, order: config?.order ?? 999 });
        }
    });
    orderedMetrics.sort((a, b) => a.order - b.order);
    const sortedKeys = orderedMetrics.map(m => m.key);
    const remainingMetrics = defaultMetrics.filter(m => !preferencesKeys.includes(m));
    return [...sortedKeys, ...remainingMetrics];
};
const ModifierInput = ({ value, onChange, onFocus, onBlur, style, disabled }) => {
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = value !== '' && value !== undefined;
    return (_jsx(NumberInput, { size: "sm", value: value, onChange: onChange, onFocus: () => { setIsFocused(true); onFocus(); }, onBlur: () => { setIsFocused(false); onBlur(); }, fullWidth: true, allowDecimal: true, disabled: disabled, rightIcon: !isFocused && hasValue ? _jsx("span", { style: { fontSize: '12px', color: '#667085' }, children: "%" }) : undefined, style: style }));
};
// ─── Component ────────────────────────────────────────────────────────────────
const PerformanceTrackerTab = () => {
    const dispatch = useDispatch();
    const data = useSelector(selectTrackerData);
    const loading = useSelector(selectTrackerLoading);
    const hasSitewiseAccess = useSelector(selectHasSitewiseAccess);
    const hasRecalculationAccess = useSelector(selectHasRecalculationAccess);
    const hasModifierAccess = useSelector(selectHasModifierAccess);
    const formulas = useSelector(selectTrackerFormulas);
    const savedModifiers = useSelector(selectSavedModifiers);
    const savedProjections = useSelector(selectSavedProjections);
    const trackerClientId = useSelector(selectTrackerClientId);
    const trackerMonth = useSelector(selectTrackerMonth);
    const preferences = useSelector(selectPreferences);
    const preferencesLoading = useSelector(selectPreferencesLoading);
    const [modifiers, setModifiers] = useState({});
    const [modifierInputs, setModifierInputs] = useState({});
    const [dirtyDates, setDirtyDates] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [isSmartProjectionEnabled, setIsSmartProjectionEnabled] = useState(false);
    const [sortConfig, setSortConfig] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [selectedDates, setSelectedDates] = useState(new Set());
    const handleRecalculateProjection = async () => {
        if (!trackerClientId || !trackerMonth)
            return;
        try {
            setIsRecalculating(true);
            const payload = { client_id: trackerClientId, month: trackerMonth };
            if (selectedDates.size > 0) {
                payload.selected_dates = Array.from(selectedDates);
            }
            await dispatch(recalculatePerformanceTracker(payload)).unwrap();
            setSelectedDates(new Set()); // Clear selection after successful recalculation
        }
        catch (err) {
            console.error('Recalculate error:', err);
        }
        finally {
            setIsRecalculating(false);
        }
    };
    // ✅ Derive network availability
    const networks = (data?.networks || {});
    const hasMetaTraffic = useMemo(() => {
        if (!networks.meta)
            return false;
        if (!data?.tableData || data.tableData.length === 0)
            return false;
        return data.tableData.some((row) => row.traffic && Number(row.traffic.meta || 0) > 0)
            || Number(data.tableTotal?.traffic?.meta || 0) > 0;
    }, [networks.meta, data?.tableData, data?.tableTotal]);
    const hasSearchTraffic = useMemo(() => {
        if (!networks.adword)
            return false;
        if (!data?.tableData || data.tableData.length === 0)
            return false;
        return data.tableData.some((row) => row.traffic && Number(row.traffic.search || 0) > 0)
            || Number(data.tableTotal?.traffic?.search || 0) > 0;
    }, [networks.adword, data?.tableData, data?.tableTotal]);
    const hasTotalTraffic = useMemo(() => {
        if (!data?.tableData || data.tableData.length === 0)
            return false;
        return data.tableData.some((row) => row.traffic && Number(row.traffic.total || 0) > 0)
            || Number(data.tableTotal?.traffic?.total || 0) > 0;
    }, [data?.tableData, data?.tableTotal]);
    // ✅ Derive hidden columns + column order from preferences
    const hiddenColumns = useMemo(() => {
        const hidden = getHiddenColumnsFromPreferences(preferences, TRACKER_NETWORK_KEY);
        if (!networks.meta)
            hidden.add('spend_meta');
        if (!networks.adword)
            hidden.add('spend_search');
        if (!networks.bing)
            hidden.add('spend_bing');
        if (!networks.criteo)
            hidden.add('spend_criteo');
        if (!hasMetaTraffic)
            hidden.add('spend_traffic_meta');
        if (!hasSearchTraffic)
            hidden.add('spend_traffic_search');
        if (!hasTotalTraffic)
            hidden.add('spend_traffic_total');
        return hidden;
    }, [preferences, networks, hasMetaTraffic, hasSearchTraffic, hasTotalTraffic]);
    const columnOrder = useMemo(() => getColumnOrderFromPreferences(preferences, TRACKER_NETWORK_KEY, ALL_TRACKER_METRICS), [preferences]);
    const activeTrackerMetrics = useMemo(() => {
        return ALL_TRACKER_METRICS.filter(metric => {
            if (metric.includes('meta') && !networks.meta)
                return false;
            if (metric.includes('search') && !networks.adword)
                return false;
            if (metric.includes('bing') && !networks.bing)
                return false;
            if (metric.includes('criteo') && !networks.criteo)
                return false;
            if (metric === 'spend_traffic_meta' && !hasMetaTraffic)
                return false;
            if (metric === 'spend_traffic_search' && !hasSearchTraffic)
                return false;
            if (metric === 'spend_traffic_total' && !hasTotalTraffic)
                return false;
            return true;
        });
    }, [networks, hasMetaTraffic, hasSearchTraffic, hasTotalTraffic]);
    // ─── Fetch Preferences on mount ───────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchPreferences({ moduleKey: 'account_performance' }));
    }, [dispatch]);
    // ─── Load saved modifiers ─────────────────────────────────────────────────
    useEffect(() => {
        if (!savedModifiers || Object.keys(savedModifiers).length === 0) {
            setModifiers({});
            setModifierInputs({});
            return;
        }
        const parsedModifiers = {};
        const parsedInputs = {};
        Object.entries(savedModifiers).forEach(([date, val]) => {
            const num = Number(val);
            if (!isNaN(num)) {
                parsedModifiers[date] = num;
                parsedInputs[date] = String(num);
            }
        });
        setModifiers(parsedModifiers);
        setModifierInputs(parsedInputs);
    }, [savedModifiers]);
    // ─── Column Visibility Change Handler ─────────────────────────────────────
    const handleColumnVisibilityChange = (newHiddenColumns) => {
        const existingPrefs = preferences?.[TRACKER_NETWORK_KEY] || [];
        const existingOrderMap = new Map();
        existingPrefs.forEach((item, index) => {
            const key = Object.keys(item)[0];
            existingOrderMap.set(key, item[key]?.order ?? index + 1);
        });
        const newPreferences = [];
        ALL_TRACKER_METRICS.forEach((metric, index) => {
            const isHidden = newHiddenColumns.has(metric);
            const existingOrder = existingOrderMap.get(metric) ?? index + 1;
            newPreferences.push({
                [metric]: {
                    visibility: !isHidden,
                    order: existingOrder,
                },
            });
        });
        dispatch(updateNetworkPreference({ networkKey: TRACKER_NETWORK_KEY, metrics: newPreferences }));
        const updatedPreferences = {
            ...preferences,
            [TRACKER_NETWORK_KEY]: newPreferences,
        };
        dispatch(savePreferences({ preferences: updatedPreferences, moduleKey: 'account_performance' }));
    };
    // ─── Column Order Change Handler ──────────────────────────────────────────
    const handleColumnOrderChange = (newOrder) => {
        const existingPrefs = preferences?.[TRACKER_NETWORK_KEY] || [];
        const existingVisibilityMap = new Map();
        existingPrefs.forEach(item => {
            const key = Object.keys(item)[0];
            existingVisibilityMap.set(key, item[key]?.visibility ?? true);
        });
        const newPreferences = [];
        // Filter out 'date' — it's always locked
        const metricsInOrder = newOrder.filter(m => m !== 'date');
        metricsInOrder.forEach((metric, index) => {
            const isVisible = existingVisibilityMap.get(metric) ?? true;
            newPreferences.push({
                [metric]: { visibility: isVisible, order: index + 1 },
            });
        });
        // Add any metrics not in newOrder
        ALL_TRACKER_METRICS.forEach((metric, index) => {
            if (!metricsInOrder.includes(metric)) {
                const isVisible = existingVisibilityMap.get(metric) ?? true;
                newPreferences.push({
                    [metric]: { visibility: isVisible, order: metricsInOrder.length + index + 1 },
                });
            }
        });
        dispatch(updateNetworkPreference({ networkKey: TRACKER_NETWORK_KEY, metrics: newPreferences }));
        const updatedPreferences = {
            ...preferences,
            [TRACKER_NETWORK_KEY]: newPreferences,
        };
        dispatch(savePreferences({ preferences: updatedPreferences, moduleKey: 'account_performance' }));
    };
    // ─── Visibility Panel Handlers ────────────────────────────────────────────
    const handleColumnVisibilityClick = (event) => {
        setColumnVisibilityAnchor(event.currentTarget);
    };
    const handleColumnVisibilityClose = () => {
        setColumnVisibilityAnchor(null);
    };
    // ─── Modifier Handlers ────────────────────────────────────────────────────
    const handleModifierChange = (date, val) => {
        setIsDirty(true);
        setDirtyDates(prev => new Set(prev).add(date));
        if (val === '' || val === undefined) {
            setModifierInputs(prev => { const next = { ...prev }; delete next[date]; return next; });
            setModifiers(prev => { const next = { ...prev }; delete next[date]; return next; });
            return;
        }
        setModifierInputs(prev => ({ ...prev, [date]: val }));
        if (val === '-' || val === '.' || val === '-.') {
            setModifiers(prev => { const next = { ...prev }; delete next[date]; return next; });
            return;
        }
        const num = Number(val);
        if (!isNaN(num)) {
            setModifiers(prev => ({ ...prev, [date]: num }));
        }
    };
    const handleModifierFocus = (date) => {
        if (modifierInputs[date] === undefined) {
            setModifierInputs(prev => ({ ...prev, [date]: '' }));
        }
    };
    const handleModifierBlur = (date) => {
        const current = modifierInputs[date];
        if (current === '' || current === '-' || current === '.' || current === '-.') {
            setModifierInputs(prev => { const next = { ...prev }; delete next[date]; return next; });
        }
        else if (current !== undefined) {
            const num = Number(current);
            if (!isNaN(num)) {
                setModifierInputs(prev => ({ ...prev, [date]: num.toFixed(2) }));
            }
        }
    };
    // ─── Save Handler ─────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!trackerClientId || !trackerMonth)
            return;
        const mcList = (data?.metrics?.measurement_channel || []).map((c) => c.toLowerCase());
        if (dirtyDates.size === 0)
            return;
        // Build entries array — send all past/actual rows so cascading modifier
        // changes are saved, PLUS any future row the user explicitly edited
        // (dirty) — a blanket future-day exclusion would silently drop
        // manually-set future modifiers before they ever reach the backend.
        const commissionUpdateEntry = tableRows.map(row => {
            const date = row.date;
            const existing = savedProjections[date];
            const projected_spend = !row?.isFuture && existing?.projected_spend?.length
                ? existing.projected_spend
                : [
                    { network: 'meta', value: Number(row?.spend_meta || 0) },
                    { network: 'search', value: Number(row?.spend_search || 0) },
                    { network: 'bing', value: Number(row?.spend_bing || 0) },
                    { network: 'criteo', value: Number(row?.spend_criteo || 0) },
                ];
            const projected_spend_sitewide = savedProjections[date]?.projected_spend_sitewide || [];
            const projected_revenue = [];
            const projected_revenue_sitewide = [];
            let revDaily = Number(row?.rev_daily || 0);
            if (!row?.isFuture && existing?.projected_revenue?.length) {
                revDaily = existing.projected_revenue.reduce((sum, r) => sum + Number(r.value || 0), 0);
            }
            const getNetworkName = () => {
                if (mcList.includes('shopify'))
                    return 'shopify';
                if (mcList.includes('ga') || mcList.includes('google analytics'))
                    return 'google analytics';
                if (mcList.some((m) => m.includes('facebook')))
                    return 'facebook ads';
                if (mcList.some((m) => m.includes('google ads') || m.includes('google')))
                    return 'google ads';
                if (mcList.some((m) => m.includes('bing')))
                    return 'bing ads';
                if (mcList.some((m) => m.includes('criteo')))
                    return 'criteo ads';
                if (mcList.length > 0)
                    return mcList[0];
                return 'unknown';
            };
            const netName = getNetworkName();
            if (isSmartProjectionEnabled) {
                projected_revenue_sitewide.push({ network: netName, value: revDaily });
                const saved = savedProjections[date];
                if (saved && saved.projected_revenue) {
                    projected_revenue.push(...saved.projected_revenue);
                }
            }
            else {
                projected_revenue.push({ network: netName, value: revDaily });
                const saved = savedProjections[date];
                if (saved && saved.projected_revenue_sitewide) {
                    projected_revenue_sitewide.push(...saved.projected_revenue_sitewide);
                }
            }
            const [mm, dd, yyyy] = date.split('/');
            const dateForDb = `${yyyy}-${mm}-${dd}`;
            return {
                date: dateForDb,
                projected_spend,
                projected_spend_sitewide,
                projected_revenue,
                projected_revenue_sitewide,
                modifier: modifiers[date] !== undefined
                    ? Number(modifiers[date])
                    : Number(row?.rev_modifier_effective ?? row?.rev_modifier ?? 0),
                is_manual: modifiers[date] !== undefined
                    ? true
                    : Boolean(row?.rev_modifier !== null && row?.rev_modifier !== undefined),
            };
        });
        const entries = commissionUpdateEntry.filter((entry, idx) => {
            const row = tableRows[idx];
            return !row.isFuture || dirtyDates.has(row.date);
        });
        if (!entries.length)
            return;
        setIsSaving(true);
        try {
            await dispatch(savePerformanceTracker({
                client_id: trackerClientId,
                month: trackerMonth,
                entries,
                commissionUpdateEntry
            })).unwrap();
            setIsDirty(false);
            setDirtyDates(new Set());
            dispatch(fetchAccountPerformanceTracker({
                client_id: trackerClientId,
                month: trackerMonth
            }));
        }
        catch (e) {
            console.error('Save failed:', e);
        }
        finally {
            setIsSaving(false);
        }
    };
    // ─── Sort Handler ─────────────────────────────────────────────────────────
    const handleSortChange = (columnId, direction) => {
        setSortConfig(direction ? { columnId, direction } : null);
    };
    // ─── Cell Styling ─────────────────────────────────────────────────────────
    const getCellSx = (row) => {
        if (!row || row.isFuture === undefined)
            return {};
        return { backgroundColor: row.isFuture ? '#ffffff !important' : '#DEE9F5 !important' };
    };
    const getDateCellSx = (row) => {
        if (!row || row.isFuture === undefined)
            return { backgroundColor: '#ffffff !important', fontWeight: 400 };
        return { backgroundColor: row.isFuture ? '#ffffff !important' : '#DEE9F5 !important', fontWeight: 400 };
    };
    // ─── Columns ──────────────────────────────────────────────────────────────
    const twoLine = (l1, l2) => (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', lineHeight: 1.3 }, children: [_jsx("span", { children: l1 }), _jsx("span", { children: l2 })] }));
    // Revenue is driven by Shopify either when it's one of the ad-attributed
    // measurement channels, or — with Site Wide Access on — when the sitewide
    // revenue source itself is Shopify. Only then does the formula apply.
    const isShopifyRevenueSource = isSmartProjectionEnabled
        ? (data?.metrics?.sitewide_measurement_channel || '').toLowerCase() === 'shopify'
        : (data?.metrics?.measurement_channel || []).some((c) => c.toLowerCase() === 'shopify');
    const revDailyTooltip = isShopifyRevenueSource
        ? `Derived using: (${data?.metrics?.shopify_formula || 'G-D+S+T'})`
        : 'Daily revenue';
    const activeTrafficChannel = isSmartProjectionEnabled
        ? (data?.metrics?.sitewide_measurement_channel || '').toLowerCase()
        : (data?.metrics?.measurement_channel || []).map((c) => c.toLowerCase()).join(' ');
    const totalTrafficTooltip = data?.metrics?.sitewide_measurement_channel
        ? `${data.metrics.sitewide_measurement_channel} Sessions`
        : '';
    const columns = useMemo(() => {
        const cols = [
            // ✅ date is always locked — draggable: false, not in visibility panel
            { id: 'date', label: 'Date', align: 'left', minWidth: 120, cellSx: getDateCellSx, draggable: false },
            { id: 'spend_meta', label: 'Meta', align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'spend_search', label: 'Search', align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'spend_bing', label: 'Bing', align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'spend_criteo', label: 'Criteo', align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'spend_daily', label: 'Daily', align: 'left', minWidth: 80, cellSx: getCellSx },
            ...(hasMetaTraffic ? [{ id: 'spend_traffic_meta', label: twoLine('Meta', 'Traffic'), align: 'left', minWidth: 80, cellSx: getCellSx, tooltip: 'Outbound Click' }] : []),
            ...(hasSearchTraffic ? [{ id: 'spend_traffic_search', label: twoLine('Search', 'Traffic'), align: 'left', minWidth: 80, cellSx: getCellSx, tooltip: 'Click' }] : []),
            ...(hasTotalTraffic ? [{ id: 'spend_traffic_total', label: twoLine('Total', 'Traffic'), align: 'left', minWidth: 80, cellSx: getCellSx, tooltip: totalTrafficTooltip }] : []),
            { id: 'spend_goal_daily', label: twoLine('Original', 'Daily'), align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'spend_mtd', label: 'MTD', align: 'left', minWidth: 100, cellSx: getCellSx },
            { id: 'spend_goal_mtd', label: twoLine('Original', 'MTD'), align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'spend_var', label: 'Variance', align: 'left', minWidth: 100, cellSx: getCellSx },
            { id: 'rev_daily', label: 'Daily', align: 'left', minWidth: 80, cellSx: getCellSx, tooltip: revDailyTooltip },
            { id: 'rev_mtd', label: 'MTD', align: 'left', minWidth: 100, cellSx: getCellSx },
            { id: 'rev_modifier', label: 'Modifier', align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'rev_goal_daily', label: twoLine('Original', 'Daily'), align: 'left', minWidth: 80, cellSx: getCellSx, tooltip: '(Revenue Goal/No. of Days in Month)' },
            { id: 'rev_goal_mtd', label: twoLine('Original', 'MTD'), align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'rev_var', label: 'Variance', align: 'left', minWidth: 100, cellSx: getCellSx, tooltip: 'Variance =((Actual MTD - Goal MTD) / Goal MTD)*100)' },
            { id: 'roas_daily', label: 'Daily', align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'roas_mtd', label: 'MTD', align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'roas_goal_mtd', label: twoLine('Original', 'MTD'), align: 'left', minWidth: 80, cellSx: getCellSx },
            { id: 'roas_var', label: 'Variance', align: 'right', minWidth: 100, cellSx: getCellSx },
        ];
        return cols.map(c => ({ ...c, sortable: true }));
    }, [revDailyTooltip, totalTrafficTooltip, hasMetaTraffic, hasSearchTraffic, hasTotalTraffic]);
    // ─── Data Processing ────
    const processedData = useMemo(() => {
        if (!data?.tableData || !data?.metrics)
            return { rows: [], total: null };
        const rows = buildTrackerRows(data.tableData, data.metrics, modifiers, isSmartProjectionEnabled, formulas, savedProjections);
        const total = buildTrackerTotal(rows, data.tableTotal, data.metrics);
        return { rows, total };
    }, [data?.tableData, data?.tableTotal, data?.metrics, formulas, modifiers, isSmartProjectionEnabled, savedProjections]);
    const tableRows = processedData.rows;
    const computedTotal = processedData.total;
    const sortedTableRows = useMemo(() => {
        if (!sortConfig)
            return tableRows;
        return [...tableRows].sort((a, b) => {
            const valA = a[sortConfig.columnId];
            const valB = b[sortConfig.columnId];
            if (valA === valB)
                return 0;
            if (valA === undefined || valA === null)
                return sortConfig.direction === 'asc' ? -1 : 1;
            if (valB === undefined || valB === null)
                return sortConfig.direction === 'asc' ? 1 : -1;
            if (sortConfig.columnId === 'date') {
                const dateA = new Date(valA).getTime();
                const dateB = new Date(valB).getTime();
                if (!isNaN(dateA) && !isNaN(dateB)) {
                    return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
                }
            }
            if (typeof valA === 'number' && typeof valB === 'number') {
                return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
            }
            const strA = String(valA).toLowerCase();
            const strB = String(valB).toLowerCase();
            if (strA < strB)
                return sortConfig.direction === 'asc' ? -1 : 1;
            if (strA > strB)
                return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [tableRows, sortConfig]);
    // ─── Metric Card Values ───────────────────────────────────────────────────
    const cardValues = useMemo(() => formulas ? calcMetricCardValues(computedTotal, data?.metrics || {}, formulas) : null, [computedTotal, data?.metrics, formulas]);
    const todayIndex = tableRows.findIndex(row => row.isFuture === true);
    const yesterdayRow = todayIndex > 0
        ? tableRows[todayIndex - 1]
        : tableRows[tableRows.length - 1];
    const roasGoal = Number(data?.metrics?.roas_goal || 0);
    const roasMtdYesterday = Number(yesterdayRow?.roas_mtd || 0);
    // Actual spend so far (last non-future row) for Spend Goal card
    const actualSpendMtd = Number(yesterdayRow?.spend_mtd || 0);
    const spendGoalVal = Number(data?.metrics?.spend_goal || 0);
    const spendGoalVar = spendGoalVal > 0 ? ((actualSpendMtd - spendGoalVal) / spendGoalVal) * 100 : 0;
    const spendGoalVarText = `${Math.abs(Math.round(spendGoalVar))}%`;
    const spendGoalTrend = (spendGoalVar >= 0 ? 'up' : 'down');
    const roasGoalVarYesterday = roasGoal > 0
        ? ((roasMtdYesterday - roasGoal) / roasGoal) * 100
        : 0;
    // ─── Dynamic Goals for UI based on Site Wide Access Toggle ───
    const displayRevGoal = isSmartProjectionEnabled
        ? Number(data?.metrics?.paid_total_site_rev || 0)
        : Number(data?.metrics?.revenue_goal || 0);
    const displayRevVar = displayRevGoal > 0 ? ((cardValues?.revMtd || 0) - displayRevGoal) / displayRevGoal * 100 : 0;
    const displayRevVarText = `${Math.abs(Math.round(displayRevVar))}%`;
    const displayRevTrend = displayRevVar >= 0 ? 'up' : 'down';
    const displayRoasGoal = isSmartProjectionEnabled
        ? Number(data?.metrics?.paid_site_roas_final || 0)
        : Number(data?.metrics?.roas_goal || 0);
    const displayRoasVar = displayRoasGoal > 0 ? ((roasMtdYesterday - displayRoasGoal) / displayRoasGoal) * 100 : 0;
    const displayRoasVarText = `${Math.abs(Math.round(displayRoasVar))}%`;
    const displayRoasTrend = displayRoasVar >= 0 ? 'up' : 'down';
    // ─── Auto-scroll to yesterday / active date row on initial load ──────────
    const hasAutoScrolledRef = useRef(false);
    useEffect(() => {
        hasAutoScrolledRef.current = false;
    }, [trackerClientId, trackerMonth]);
    useEffect(() => {
        if (loading || preferencesLoading || !yesterdayRow?.date || sortedTableRows.length === 0 || hasAutoScrolledRef.current) {
            return;
        }
        const timer = setTimeout(() => {
            const targetDate = yesterdayRow.date;
            const targetEl = document.querySelector(`[data-date="${targetDate}"]`);
            if (targetEl) {
                const scrollContainer = targetEl.closest('.MuiTableContainer-root') || targetEl.closest('.reusable-table-container');
                if (scrollContainer) {
                    const containerRect = scrollContainer.getBoundingClientRect();
                    const targetRect = targetEl.getBoundingClientRect();
                    const offset = targetRect.top - containerRect.top + scrollContainer.scrollTop - 45;
                    scrollContainer.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
                    hasAutoScrolledRef.current = true;
                }
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [loading, preferencesLoading, yesterdayRow?.date, sortedTableRows.length]);
    // ─── Loading / Empty States ───────────────────────────────────────────────
    if (loading || preferencesLoading) {
        return (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', p: 4 }, children: _jsx(CommonLoader, { className: 'table-loader' }) }));
    }
    if (!data)
        return null;
    if (data.no_goals) {
        return (_jsx(Box, { sx: { p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: '8px', border: '1px solid #EAECF0', mt: 2 }, children: _jsx(Typography, { sx: { color: '#475467', fontSize: '14px' }, children: "No goals have been added for this client" }) }));
    }
    const { metrics } = data;
    // ─── Toggle Visibility Logic ──────────────────────────────────────────────
    const measurementChannels = (metrics?.measurement_channel || []).map((c) => c.toLowerCase());
    const hasRestrictedChannel = measurementChannels.includes('shopify')
        || measurementChannels.includes('ga')
        || measurementChannels.includes('google analytics');
    const hasAdvancedData = metrics?.has_advanced_data === true;
    const shouldShowToggle = !hasRestrictedChannel && hasSitewiseAccess && hasAdvancedData;
    const formatMoney = (val) => Math.round(val).toLocaleString();
    const metaPercent = Number(metrics.meta_spend_percent || 0);
    const adwordPercent = Number(metrics.adword_spend_percent || 0);
    const bingPercent = Number(metrics.bing_spend_percent || 0);
    const criteoPercent = Number(metrics.criteo_spend_percent || 0);
    // ─── Variance Badge ───────────────────────────────────────────────────────
    const renderVarianceBadge = (numVal, fontWeight = 500) => {
        const roundedVal = Math.round(numVal);
        const trend = roundedVal > 0 ? 'up' : roundedVal < 0 ? 'down' : 'neutral';
        const bgcolor = trend === 'down' ? '#FEF3F2' : trend === 'up' ? '#ECFDF3' : '#F2F4F7';
        const color = trend === 'down' ? '#B42318' : trend === 'up' ? '#027A48' : '#475467';
        return (_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-start' }, children: _jsxs(Box, { sx: {
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1, py: '2px', borderRadius: '16px', bgcolor, color
                }, children: [_jsx(StyledCurrency, { value: `${Math.abs(roundedVal)}%`, valueColor: color, symbolColor: color, fontSize: "14px", symbolSize: "14px", valueWeight: fontWeight, symbolWeight: fontWeight }), trend === 'up' && _jsx("img", { src: "/assets/trend-up.svg", alt: "Up", width: 12, height: 12 }), trend === 'down' && _jsx("img", { src: "/assets/trending-down.svg", alt: "Down", width: 12, height: 12 })] }) }));
    };
    // ─── Cell Renderer ────────────────────────────────────────────────────────
    const renderCell = (row, column) => {
        const val = row[column.id];
        if (column.id === 'rev_modifier') {
            const isAnchor = modifiers[row.date] !== undefined;
            const hasEffective = row.rev_modifier_has_effective === true;
            const format2Dec = (v) => {
                if (v === undefined || v === null || v === '')
                    return '';
                const n = Number(v);
                return !isNaN(n) ? n.toFixed(2) : String(v);
            };
            // Past days with no saved modifier fall through here with val=null —
            // show 0.00 instead of a blank box (matches the Total row, which
            // already renders 0 for this column when nothing was saved).
            const displayValue = modifierInputs[row.date] !== undefined
                ? modifierInputs[row.date]
                : (format2Dec(val) || '0.00');
            if (!isAnchor && hasEffective) {
                const effectiveDisplay = modifierInputs[row.date] !== undefined
                    ? modifierInputs[row.date]
                    : format2Dec(row.rev_modifier_effective ?? 0);
                return (_jsx("div", { style: { width: '85px' }, children: _jsx(ModifierInput, { value: effectiveDisplay, onChange: (e) => handleModifierChange(row.date, e.target.value), onFocus: () => handleModifierFocus(row.date), onBlur: () => handleModifierBlur(row.date), disabled: !hasModifierAccess, style: { textAlign: 'right', fontSize: '13px', fontWeight: 500, color: '#667085' } }) }));
            }
            return (_jsx("div", { style: { width: '85px' }, children: _jsx(ModifierInput, { value: displayValue, onChange: (e) => handleModifierChange(row.date, e.target.value), onFocus: () => handleModifierFocus(row.date), onBlur: () => handleModifierBlur(row.date), disabled: !hasModifierAccess, style: { textAlign: 'right', fontSize: '13px', fontWeight: 500 } }) }));
        }
        if (val === undefined || val === null)
            return '-';
        if (column.id === 'date')
            return val;
        const numVal = Number(val);
        if (column.id.includes('var'))
            return renderVarianceBadge(numVal);
        const channelSpendCols = ['spend_meta', 'spend_search', 'spend_bing', 'spend_criteo', 'spend_daily'];
        if (channelSpendCols.includes(column.id)) {
            const saved = savedProjections[row.date];
            let bottomVal = 0;
            if (saved && !row.isFuture) {
                const projSpendArray = isSmartProjectionEnabled && saved.projected_spend_sitewide?.length
                    ? saved.projected_spend_sitewide
                    : (saved.projected_spend || []);
                if (column.id === 'spend_meta') {
                    const item = projSpendArray.find((n) => n.network === 'facebook ads' || n.network === 'meta');
                    bottomVal = item ? Number(item.value || 0) : 0;
                }
                else if (column.id === 'spend_search') {
                    const item = projSpendArray.find((n) => n.network === 'google ads' || n.network === 'adword' || n.network === 'search');
                    bottomVal = item ? Number(item.value || 0) : 0;
                }
                else if (column.id === 'spend_bing') {
                    const item = projSpendArray.find((n) => n.network === 'bing ads' || n.network === 'bing');
                    bottomVal = item ? Number(item.value || 0) : 0;
                }
                else if (column.id === 'spend_criteo') {
                    const item = projSpendArray.find((n) => n.network === 'criteo ads' || n.network === 'criteo');
                    bottomVal = item ? Number(item.value || 0) : 0;
                }
                else if (column.id === 'spend_daily') {
                    bottomVal = projSpendArray.reduce((sum, n) => sum + Number(n.value || 0), 0);
                }
            }
            return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }, children: [_jsx(StyledCurrency, { value: `$${formatMoney(numVal)}`, fontSize: "14px", valueWeight: 400 }), !row.isFuture && (_jsx(StyledCurrency, { value: `$${formatMoney(bottomVal)}`, fontSize: "13px", valueWeight: 400, valueColor: "#98A2B3", symbolColor: "#98A2B3" }))] }));
        }
        if (column.id === 'rev_daily' || column.id === 'rev_mtd') {
            let bottomVal = 0;
            if (!row.isFuture) {
                if (column.id === 'rev_daily') {
                    const saved = savedProjections[row.date];
                    if (saved) {
                        const projRevArray = isSmartProjectionEnabled && saved.projected_revenue_sitewide?.length
                            ? saved.projected_revenue_sitewide
                            : (saved.projected_revenue || []);
                        bottomVal = projRevArray.reduce((sum, n) => sum + Number(n.value || 0), 0);
                    }
                }
                else {
                    bottomVal = row.rev_mtd_predicted !== undefined && row.rev_mtd_predicted !== null
                        ? Number(row.rev_mtd_predicted)
                        : Number(row.rev_goal_mtd || 0);
                }
            }
            return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }, children: [_jsx(StyledCurrency, { value: `$${formatMoney((column.id === 'rev_daily' && row.isFuture) ? Math.round(numVal) : numVal)}`, fontSize: "14px", valueWeight: 400 }), !row.isFuture && (_jsx(StyledCurrency, { value: `$${formatMoney(bottomVal)}`, fontSize: "13px", valueWeight: 400, valueColor: "#98A2B3", symbolColor: "#98A2B3" }))] }));
        }
        if (column.id === 'roas_daily') {
            const saved = savedProjections[row.date];
            let bottomVal = 0;
            if (saved && !row.isFuture) {
                const projRevArray = isSmartProjectionEnabled && saved.projected_revenue_sitewide?.length
                    ? saved.projected_revenue_sitewide
                    : (saved.projected_revenue || []);
                const dailyRev = projRevArray.reduce((sum, n) => sum + Number(n.value || 0), 0);
                const projSpendArray = isSmartProjectionEnabled && saved.projected_spend_sitewide?.length
                    ? saved.projected_spend_sitewide
                    : (saved.projected_spend || []);
                const dailySpend = projSpendArray.reduce((sum, n) => sum + Number(n.value || 0), 0);
                bottomVal = dailySpend > 0 ? dailyRev / dailySpend : 0;
            }
            return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }, children: [_jsx(Typography, { sx: { fontSize: '14px !important', fontWeight: 400, color: '#101828', lineHeight: 1 }, children: numVal.toFixed(2) }), !row.isFuture && (_jsx(Typography, { sx: { fontSize: '13px !important', fontWeight: 400, color: '#98A2B3', lineHeight: 1 }, children: bottomVal.toFixed(2) }))] }));
        }
        if (column.id.includes('traffic')) {
            return _jsx(Typography, { sx: { fontSize: '14px !important', fontWeight: 400, color: '#101828' }, children: numVal.toLocaleString() });
        }
        if (column.id.includes('rev') || column.id.includes('spend')) {
            return _jsx(StyledCurrency, { value: `$${formatMoney(numVal)}`, fontSize: "14px", valueWeight: 400 });
        }
        if (column.id.includes('roas'))
            return numVal.toFixed(2);
        return val;
    };
    // ─── Summary Row Renderer ─────────────────────────────────────────────────
    const renderSummaryRow = (_rows, column) => {
        if (!computedTotal)
            return '-';
        if (column.id === 'date') {
            return _jsx(Typography, { sx: { fontWeight: 700, fontSize: '14px !important' }, children: "Total" });
        }
        const totalRow = {
            spend_meta: computedTotal.spend?.meta,
            spend_search: computedTotal.spend?.search,
            spend_bing: computedTotal.spend?.bing,
            spend_criteo: computedTotal.spend?.criteo,
            spend_daily: computedTotal.spend?.daily,
            spend_mtd: computedTotal.spend?.mtd,
            spend_goal_daily: computedTotal.spend?.originalDaily,
            spend_goal_mtd: computedTotal.spend?.originalMtd,
            spend_var: computedTotal.spend?.variance,
            rev_daily: computedTotal.revenue?.daily,
            rev_modifier: null,
            rev_mtd: computedTotal.revenue?.mtd,
            rev_goal_daily: computedTotal.revenue?.originalDaily,
            rev_goal_mtd: computedTotal.revenue?.originalMtd,
            rev_var: computedTotal.revenue?.variance,
            roas_daily: computedTotal.roas?.daily,
            roas_mtd: computedTotal.roas?.mtd,
            roas_goal_mtd: computedTotal.roas?.originalMtd,
            roas_var: computedTotal.roas?.variance,
            spend_traffic_meta: computedTotal.traffic?.meta,
            spend_traffic_search: computedTotal.traffic?.search,
            spend_traffic_total: computedTotal.traffic?.total,
        };
        if (column.id === 'rev_modifier') {
            return _jsx(Typography, { sx: { fontSize: '14px !important', fontWeight: 700 }, children: "0" });
        }
        const val = totalRow[column.id];
        if (val === undefined || val === null)
            return '-';
        const numVal = Number(val);
        if (column.id.includes('var'))
            return renderVarianceBadge(numVal, 600);
        if (column.id.includes('traffic')) {
            return _jsx(Typography, { sx: { fontSize: '14px !important', fontWeight: 700 }, children: numVal.toLocaleString() });
        }
        if (column.id.includes('rev') || column.id.includes('spend')) {
            return _jsx(StyledCurrency, { value: `$${formatMoney(numVal)}`, fontSize: "14px", valueWeight: 700 });
        }
        if (column.id.includes('roas')) {
            return (_jsx(Typography, { sx: { fontSize: '14px !important', fontWeight: 700 }, children: numVal.toFixed(2) }));
        }
        return _jsx(Typography, { sx: { fontSize: '14px !important', fontWeight: 700 }, children: val });
    };
    const handleExport = () => {
        if (!sortedTableRows || sortedTableRows.length === 0)
            return;
        const getLabel = (label) => {
            if (typeof label === 'string')
                return label;
            if (label && label.props && label.props.children) {
                const kids = label.props.children;
                if (Array.isArray(kids)) {
                    return kids.map((k) => k?.props?.children || k).join(' ');
                }
                return String(kids);
            }
            return String(label);
        };
        const formatExportValue = (val, colId) => {
            if (val === undefined || val === null)
                return '-';
            if (colId === 'date' && val === 'Total')
                return val;
            if (typeof val === 'number') {
                if (colId.includes('var') || colId.includes('percent'))
                    return Number(val.toFixed(2));
                if (colId.includes('roas'))
                    return Number(val.toFixed(2));
                return Math.round(val);
            }
            return val;
        };
        const visibleCols = columns.filter(col => !hiddenColumns.has(col.id));
        const headers = visibleCols.map(col => getLabel(col.label));
        const dataRows = sortedTableRows.map(row => {
            return visibleCols.map(col => {
                let val = row[col.id];
                return formatExportValue(val, col.id);
            });
        });
        const totalRow = {
            spend_meta: computedTotal?.spend?.meta,
            spend_search: computedTotal?.spend?.search,
            spend_bing: computedTotal?.spend?.bing,
            spend_criteo: computedTotal?.spend?.criteo,
            spend_daily: computedTotal?.spend?.daily,
            spend_mtd: computedTotal?.spend?.mtd,
            spend_goal_daily: computedTotal?.spend?.originalDaily,
            spend_goal_mtd: computedTotal?.spend?.originalMtd,
            spend_var: computedTotal?.spend?.variance,
            rev_daily: computedTotal?.revenue?.daily,
            rev_modifier: null,
            rev_mtd: computedTotal?.revenue?.mtd,
            rev_goal_daily: computedTotal?.revenue?.originalDaily,
            rev_goal_mtd: computedTotal?.revenue?.originalMtd,
            rev_var: computedTotal?.revenue?.variance,
            roas_daily: computedTotal?.roas?.daily,
            roas_mtd: computedTotal?.roas?.mtd,
            roas_goal_mtd: computedTotal?.roas?.originalMtd,
            roas_var: computedTotal?.roas?.variance,
            spend_traffic_meta: computedTotal?.traffic?.meta,
            spend_traffic_search: computedTotal?.traffic?.search,
            spend_traffic_total: computedTotal?.traffic?.total,
        };
        const summaryRow = visibleCols.map(col => {
            if (col.id === 'date')
                return 'Total';
            if (col.id === 'rev_modifier')
                return '0';
            return formatExportValue(totalRow[col.id], col.id);
        });
        const headerRow = headers.map(h => ({
            v: h,
            t: 's',
            s: { font: { bold: true, color: { rgb: '000000' } }, fill: { fgColor: { rgb: 'D9D9D9' } } },
        }));
        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows, summaryRow]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Performance Tracker');
        XLSX.writeFile(wb, `Performance_Tracker.xlsx`);
    };
    // ─── Render ───────────────────────────────────────────────────────────────
    return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsxs(Grid, { container: true, spacing: 2, className: "performance-matrics", children: [_jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(MetricCard, { title: "Spend Type", value: metrics.spend_type?.[0] || 'N/A', sx: { height: '100%' }, icon: _jsx("img", { src: "/assets/spend-type.svg", alt: "icon", style: { width: 18, height: 18 }, onError: (e) => (e.currentTarget.style.display = 'none') }), valuePill: true }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(MetricCard, { title: "Measurement Channel", value: isSmartProjectionEnabled
                                ? (metrics.sitewide_measurement_channel || 'N/A')
                                : ((metrics.measurement_channel || []).map((c) => c.toLowerCase() === 'facebook ads' || c.toLowerCase() === 'facebook' ? 'Meta' : c).join(', ') || 'N/A'), sx: { height: '100%' }, icon: _jsx("img", { src: "/assets/meas-channel.svg", alt: "icon", style: { width: 18, height: 18 }, onError: (e) => (e.currentTarget.style.display = 'none') }), valuePill: true }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(MetricCard, { title: "Revenue Goal", value: Math.round(displayRevGoal).toLocaleString(), prefix: "$", tooltipText: "The target revenue for this month", sx: { height: '100%' }, icon: _jsx("img", { src: "/assets/stats/revenue.svg", alt: "icon", style: { width: 18, height: 18 }, onError: (e) => (e.currentTarget.style.display = 'none') }), comparisonValue: cardValues?.revMtd?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || "0", comparisonPrefix: "$", percentageText: displayRevVarText, trend: displayRevTrend }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: cardValues.isBudgetBased ? (_jsx(MetricCard, { title: "Spend Goal", value: Math.round(Number(metrics.spend_goal || 0)).toLocaleString(), prefix: "$", tooltipText: "The target spend for this month", sx: { height: '100%' }, icon: _jsx("img", { src: "/assets/stats/revenue.svg", alt: "icon", style: { width: 18, height: 18 }, onError: (e) => (e.currentTarget.style.display = 'none') }), comparisonValue: actualSpendMtd.toLocaleString(undefined, { maximumFractionDigits: 0 }), comparisonPrefix: "$", percentageText: spendGoalVarText, trend: spendGoalTrend })) : (_jsx(MetricCard, { title: "ROAS Goal", value: displayRoasGoal.toFixed(2), tooltipText: "The target Return on Ad Spend for this month", sx: { height: '100%' }, icon: _jsx("img", { src: "/assets/stats/roas.svg", alt: "icon", style: { width: 18, height: 18 }, onError: (e) => (e.currentTarget.style.display = 'none') }), comparisonValue: roasMtdYesterday.toFixed(2), percentageText: displayRoasVarText, trend: displayRoasTrend })) }), (() => {
                        const activeCards = [
                            { label: 'Meta Spend', networkKey: 'meta', icon: '/assets/meta.svg', spend: cardValues.metaSpend, percent: metaPercent },
                            { label: 'Adword Spend', networkKey: 'adword', icon: '/assets/adwords.svg', spend: cardValues.adwordSpend, percent: adwordPercent },
                            { label: 'Bing Spend', networkKey: 'bing', icon: '/assets/bing.svg', spend: cardValues.bingSpend, percent: bingPercent },
                            { label: 'Criteo Spend', networkKey: 'criteo', icon: '/assets/criteo.svg', spend: cardValues.criteoSpend, percent: criteoPercent },
                        ].filter(({ networkKey }) => !!data?.networks?.[networkKey]);
                        const colSize = Math.floor(12 / (activeCards.length || 1));
                        return activeCards.map(({ label, icon, spend, percent }) => (_jsx(Grid, { size: { xs: 12, sm: 6, md: colSize }, children: _jsxs(Box, { sx: {
                                    border: '1px solid #EAECF0', borderRadius: '8px',
                                    p: '10px 12px', display: 'flex', alignItems: 'center',
                                    justifyContent: 'space-between', height: '100%', bgcolor: '#fff'
                                }, children: [_jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', flexShrink: 0 }, children: _jsx("img", { src: icon, alt: "icon", style: { width: 18, height: 18 }, onError: (e) => (e.currentTarget.style.display = 'none') }) }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column' }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontWeight: 500, color: '#475467', lineHeight: '20px' }, children: label }), _jsx(InfoTooltip, { title: _jsxs(Box, { className: "all-Font-Sizes-12", sx: { p: 0.5 }, children: ["Calculation: (Spend Goal x ", percent, "%)"] }), placement: "top" })] }), percent > 0 && (_jsx(StyledCurrency, { value: `$${formatMoney(spend)}`, symbolWeight: 500, fontSize: "14px", symbolSize: "14px", symbolColor: "#98A2B3", valueColor: "#667085", className: "all-Font-Sizes-12", style: { lineHeight: '20px', display: 'inline-block' } }))] })] }), _jsx(StyledCurrency, { value: `${percent}%`, valueWeight: 700, symbolWeight: 600, fontSize: "16px", symbolSize: "14px", symbolColor: "#344054", valueColor: "#344054" })] }) }, label)));
                    })()] }), _jsxs(Box, { sx: { border: '1px solid #EAECF0', borderRadius: '8px', overflow: 'hidden', bgcolor: '#fff', mt: 1 }, children: [_jsx(TableToolbar, { title: "", showSearch: false, showRows: true, showExpand: true, showFullScreen: true, enableColumnVisibility: true, onColumnVisibilityClick: handleColumnVisibilityClick, customActionsLeft: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center' }, children: [hasRecalculationAccess && (_jsx(AppButton, { className: "app-common-button", size: "small", onClick: handleRecalculateProjection, loading: isRecalculating, disabled: isRecalculating, sx: { mr: 2, height: '36px', whiteSpace: 'nowrap', fontSize: '13px', fontWeight: 500 }, children: "Recalculate Projection" })), shouldShowToggle && (_jsx(Box, { sx: {
                                        display: 'flex', alignItems: 'center',
                                        border: '1px solid #E4E7EC', borderRadius: '8px',
                                        px: 1.5, py: 0.5, bgcolor: '#fff', mr: 2
                                    }, children: _jsx(FormControlLabel, { control: _jsx(IOSSwitch, { checked: isSmartProjectionEnabled, onChange: (e) => setIsSmartProjectionEnabled(e.target.checked), sx: { mr: 1 } }), label: _jsx(Typography, { sx: { fontSize: '13px', fontWeight: 500, color: '#475467', whiteSpace: 'nowrap' }, children: "Site Wide Access" }), sx: { m: 0 } }) })), _jsx(Box, { sx: {
                                        display: 'flex', gap: 2, alignItems: 'center',
                                        border: '1px solid #E4E7EC', borderRadius: '8px',
                                        px: 2, py: 1, bgcolor: '#fff', mr: 2
                                    }, children: [
                                        { color: '#434343', label: 'Spend' },
                                        { color: '#878787', label: 'Revenue' },
                                        { color: '#C9C9C9', label: 'ROAS' },
                                        { color: '#ffffff', label: 'Projected', border: '1px solid #D0D5DD' },
                                        { color: '#DEE9F5', label: 'Actual' },
                                    ].map(({ color, label, border }) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Box, { sx: { width: 12, height: 12, borderRadius: '50%', bgcolor: color, border: border || 'none' } }), _jsx(Typography, { sx: { fontSize: '13px', color: '#475467', fontWeight: 500 }, children: label })] }, label))) })] }), customActions: _jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' }, children: [_jsx(ExportButton, { onClick: handleExport }), hasModifierAccess && (_jsx(AppButton, { className: "app-common-button", disabled: !isDirty || isSaving, onClick: handleSave, sx: { height: '32px', minHeight: '32px', whiteSpace: 'nowrap', minWidth: '140px' }, children: isSaving ? 'Saving...' : 'Change Modifier' }))] }) }), _jsx(Box, { sx: {
                            '& .MuiTableCell-root': { borderRight: '1px solid #EAECF0', borderBottom: '1px solid #EAECF0' },
                            '& td[data-column-id="date"]': { position: 'sticky', left: 0, zIndex: '25 !important', borderRight: '2px solid #EAECF0' },
                            '& th[data-column-id="date"]': { position: 'sticky', left: 0, zIndex: '30 !important', backgroundColor: '#ffffff !important', borderRight: '2px solid #EAECF0' },
                            '& .reusable-table-summary-row': { zIndex: '30 !important' },
                            '& .reusable-table-summary-row td:first-of-type': { position: 'sticky', left: 0, zIndex: '31 !important', backgroundColor: '#F4F4F5 !important', borderRight: '2px solid #EAECF0' },
                            '& .reusable-table-container .reusable-table .reusable-table-header-cell': { backgroundColor: '#434343 !important' },
                            '& .reusable-table-container .reusable-table .reusable-table-header-cell[data-column-id^="spend_"]': { backgroundColor: '#434343 !important' },
                            '& .reusable-table-container .reusable-table .reusable-table-header-cell[data-column-id^="rev_"]': { backgroundColor: '#878787 !important' },
                            '& .reusable-table-container .reusable-table .reusable-table-header-cell[data-column-id^="roas_"]': { backgroundColor: '#C9C9C9 !important' },
                            '& .MuiTableRow-root:hover td': { backgroundColor: '#F3F4F6 !important' },
                            '& .reusable-table-summary-row td': { backgroundColor: '#F4F4F5 !important' },
                            '& .reusable-table-header-label': { color: '#fff !important' },
                            '& .reusable-table-container .reusable-table th svg path': { stroke: '#fff !important' },
                        }, children: _jsx(CommonTable, { columns: columns, rows: sortedTableRows, renderCell: renderCell, rowProps: (row) => ({ 'data-date': row.date }), stickyHeader: true, maxHeight: 645, enableSummaryRow: true, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM, renderSummaryRow: renderSummaryRow, isLoading: false, expandTrigger: EXPAND_TRIGGERS.NONE, enableColumnPinning: true, pinnedColumns: { left: ['date'], right: [] }, 
                            // ✅ Row Selection
                            enableSelection: hasRecalculationAccess, selectedRows: selectedDates, onSelectionChange: setSelectedDates, isRowSelectable: (row) => !row.isFuture, 
                            // ✅ Column Visibility from Preferences
                            hiddenColumns: hiddenColumns, onColumnVisibilityChange: handleColumnVisibilityChange, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: handleColumnVisibilityClose, columnVisibilityConfig: {
                                enable: true,
                                // ✅ date excluded — always locked/pinned
                                columns: activeTrackerMetrics,
                                enableReorder: true,
                            }, 
                            // ✅ Column Reorder from Preferences
                            enableColumnReorder: true, columnOrder: ['date', ...columnOrder], onColumnOrderChange: handleColumnOrderChange, sortConfig: sortConfig, onSortChange: handleSortChange }) })] })] }));
};
export default PerformanceTrackerTab;
